<template>
  <v-dialog :value="dialog" :fullscreen="$vuetify.breakpoint.xsOnly" max-width="760px" persistent scrollable @input="onDialogInput">
    <v-card dark class="edit-dialog-card">
      <header class="dialog-header">
        <div class="dialog-icon">
          <v-icon color="#009640">mdi-file-edit-outline</v-icon>
        </div>
        <div class="dialog-heading">
          <div class="dialog-eyebrow">Dokumentenablage</div>
          <h2>Dokument bearbeiten</h2>
          <p>Rechnungsdaten, Zuordnung und Fälligkeit aktualisieren.</p>
        </div>
        <v-btn icon class="dialog-close" aria-label="Bearbeiten schließen" @click="$emit('close-document')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </header>

      <div class="dialog-scroll">
        <section class="ai-import-panel" :class="`is-${analysisState}`" aria-live="polite">
          <div class="ai-import-icon">
            <v-progress-circular v-if="analyzing" indeterminate color="#65c98f" size="24" width="2"></v-progress-circular>
            <v-icon v-else color="#65c98f">{{ analysisState === "done" ? "mdi-check-circle-outline" : "mdi-auto-fix" }}</v-icon>
          </div>
          <div class="ai-import-copy">
            <strong>{{ analysisTitle }}</strong>
            <span>{{ analysisCopy }}</span>
          </div>
          <v-btn outlined color="#65c98f" class="ai-import-button" :loading="analyzing" :disabled="!localDocument._id" @click="startAiAnalysis">
            <v-icon left small>mdi-auto-fix</v-icon>
            AI importieren
          </v-btn>
        </section>

        <v-form ref="form">
          <section class="form-section">
            <div class="section-heading">
              <div class="section-icon"><v-icon small>mdi-text-box-outline</v-icon></div>
              <div>
                <h3>Allgemein</h3>
                <p>Bezeichnung und organisatorische Zuordnung.</p>
              </div>
            </div>

            <v-row>
              <v-col cols="12" sm="7">
                <v-text-field
                  v-model="localDocument.title"
                  label="Titel"
                  outlined
                  dense
                  hide-details="auto"
                  class="app-field"
                  :rules="[rules.required]"></v-text-field>
              </v-col>
              <v-col cols="12" sm="5">
                <v-autocomplete
                  v-model="localDocument.studio"
                  :items="mappedStudios"
                  label="Studio"
                  item-text="name"
                  item-value="_id"
                  clearable
                  outlined
                  dense
                  hide-details="auto"
                  class="app-field"
                  :rules="[rules.required]"></v-autocomplete>
              </v-col>
              <v-col v-show="!hideDescription" cols="12">
                <v-textarea
                  v-model="localDocument.description"
                  label="Beschreibung"
                  rows="2"
                  auto-grow
                  outlined
                  dense
                  hide-details="auto"
                  class="app-field"></v-textarea>
              </v-col>
            </v-row>
          </section>

          <section class="form-section">
            <div class="section-heading">
              <div class="section-icon"><v-icon small>mdi-receipt-text-outline</v-icon></div>
              <div>
                <h3>Rechnungsdaten</h3>
                <p>Angaben für Prüfung, Suche und Ablage.</p>
              </div>
            </div>

            <v-row>
              <v-col cols="12" sm="7">
                <v-autocomplete
                  v-model="localSelectedInvoiceIssuer"
                  return-object
                  :items="invoiceIssuerOptions"
                  :filter="customFilter"
                  label="Rechnungssteller"
                  item-text="title"
                  item-value="_id"
                  clearable
                  outlined
                  dense
                  hide-details="auto"
                  class="app-field"
                  :search-input.sync="localIssuerSearch"
                  :rules="[rules.required]"
                  @change="onInvoiceIssuerChange"
                  @keydown.enter.prevent>
                  <template v-slot:no-data>
                    <v-list-item v-if="localIssuerSearch" class="create-option" @click="onCreateInvoiceIssuer(localIssuerSearch)">
                      <v-list-item-icon>
                        <v-icon color="#009640">mdi-plus-circle-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>„{{ localIssuerSearch }}“ hinzufügen</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" sm="5">
                <v-autocomplete
                  v-model="localDocument.category"
                  :items="categories"
                  label="Kategorie"
                  clearable
                  outlined
                  dense
                  hide-details="auto"
                  class="app-field"
                  @change="onCategoryChange"
                  :rules="[rules.required]"></v-autocomplete>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="localDocument.amount"
                  label="Betrag"
                  type="number"
                  prepend-inner-icon="mdi-cash"
                  outlined
                  dense
                  hide-details="auto"
                  class="app-field"
                  @input="onAmountChange"
                  :rules="[rules.required]"></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="localDocument.invoiceNumber"
                  label="Rechnungsnummer"
                  prepend-inner-icon="mdi-pound"
                  outlined
                  dense
                  hide-details="auto"
                  class="app-field"
                  :rules="[rules.required]"></v-text-field>
              </v-col>
            </v-row>
          </section>

          <section class="form-section">
            <div class="section-heading">
              <div class="section-icon"><v-icon small>mdi-calendar-range-outline</v-icon></div>
              <div>
                <h3>Termine</h3>
                <p>Rechnungs- und Fälligkeitsdatum festlegen.</p>
              </div>
            </div>

            <v-row>
              <v-col cols="12" sm="6">
                <v-menu ref="menu" v-model="menuLocal" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="localDocument.date"
                      label="Rechnungsdatum"
                      prepend-inner-icon="mdi-calendar-outline"
                      readonly
                      outlined
                      dense
                      hide-details="auto"
                      class="app-field"
                      v-bind="attrs"
                      v-on="on"
                      :rules="[rules.required]"></v-text-field>
                  </template>
                  <v-date-picker v-model="localDocument.date" color="#009640" @input="onDateSelect"></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6">
                <v-menu
                  ref="menuDueDate"
                  v-model="dueDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto">
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="localDocument.dueDate"
                      label="Fälligkeitsdatum"
                      prepend-inner-icon="mdi-calendar-clock-outline"
                      readonly
                      outlined
                      dense
                      hide-details="auto"
                      class="app-field"
                      v-bind="attrs"
                      v-on="on"
                      :rules="[rules.required]"></v-text-field>
                  </template>
                  <v-date-picker v-model="localDocument.dueDate" color="#009640" @input="dueDateMenu = false"></v-date-picker>
                </v-menu>
              </v-col>
            </v-row>
          </section>
        </v-form>
      </div>

      <footer class="dialog-footer">
        <v-btn text class="secondary-button" @click="$emit('close-document')"> Abbrechen </v-btn>
        <v-btn color="#009640" depressed class="primary-button" :disabled="analyzing" @click="onSave">
          <v-icon left small>mdi-content-save-outline</v-icon>
          Speichern
        </v-btn>
      </footer>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState } from "vuex"
import api from "@/api"
import documentAiService from "@/services/documentAiService"
import documentCategoryService from "@/services/documentCategoryService"
import documentCategories from "@/constants/documentCategories"

export default {
  name: "DocumentDialog",
  props: {
    dialog: {
      type: Boolean,
      required: true,
    },
    hideDescription: {
      type: Boolean,
      default: false,
    },
    documentData: {
      type: Object,
      required: true,
    },
    invoiceIssuerOptions: {
      type: Array,
      required: true,
    },
    selectedInvoiceIssuer: {
      type: Object,
      default: null,
    },
    issuerSearch: {
      type: String,
      default: "",
    },
    menu: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      rules: {
        required: (value) => !!value || "Pflichtfeld",
      },
      localDocument: { ...this.documentData },
      menuLocal: this.menu,
      dueDateMenu: false,
      localSelectedInvoiceIssuer: this.selectedInvoiceIssuer,
      localIssuerSearch: this.issuerSearch,
      analysisState: "idle",
      analysisError: "",
      analysisRunId: 0,
      categoryTouched: false,
      suggestedCategory: null,
      categories: documentCategories,
    }
  },
  watch: {
    documentData: {
      immediate: true,
      handler(newVal) {
        const documentChanged = !this.localDocument || this.localDocument._id !== newVal._id
        this.localDocument = { ...newVal }
        if (documentChanged) {
          this.analysisRunId += 1
          this.analysisState = "idle"
          this.analysisError = ""
          this.categoryTouched = false
          this.suggestedCategory = null
        }
        if (this.localDocument.dueDate) {
          this.localDocument.dueDate = new Date(this.localDocument.dueDate).toISOString().substr(0, 10)
        }
        if (this.localDocument.date) {
          this.localDocument.date = new Date(this.localDocument.date).toISOString().substr(0, 10)
        }
      },
    },
    selectedInvoiceIssuer(newVal) {
      this.localSelectedInvoiceIssuer = newVal
    },
    issuerSearch(newVal) {
      this.localIssuerSearch = newVal
    },
    menu(newVal) {
      this.menuLocal = newVal
    },
    dialog(newVal) {
      if (!newVal) {
        this.analysisRunId += 1
        this.analysisState = "idle"
        this.analysisError = ""
        this.categoryTouched = false
        this.suggestedCategory = null
      }
    },
  },
  methods: {
    onDialogInput(value) {
      if (!value) this.$emit("close-document")
    },
    customFilter(item, queryText, itemText) {
      return itemText.toString().toLowerCase().includes(queryText.toLowerCase())
    },
    onDateSelect(date) {
      this.$refs.menu.save(date)
    },
    onInvoiceIssuerChange() {
      this.$nextTick(() => this.applyIssuerCategory())
    },
    onAmountChange() {
      this.$nextTick(() => this.applyIssuerCategory())
    },
    onCategoryChange() {
      this.categoryTouched = true
      this.suggestedCategory = null
    },
    applyIssuerCategory(force = false) {
      if (this.categoryTouched && !force) return

      const category = documentCategoryService.resolveCategory(this.localSelectedInvoiceIssuer, this.localDocument.amount)
      if (category) {
        this.localDocument.category = category
      } else if (this.suggestedCategory && this.localDocument.category === this.suggestedCategory) {
        this.localDocument.category = null
      }
      this.suggestedCategory = category
    },
    applyAnalysisResult(result) {
      // Only import supported fields; title and issuer always remain user-controlled.
      if (result.description) this.localDocument.description = result.description
      if (typeof result.amount === "number") this.localDocument.amount = result.amount
      if (result.invoiceNumber) this.localDocument.invoiceNumber = result.invoiceNumber
      if (result.date) this.localDocument.date = result.date
      if (result.dueDate) this.localDocument.dueDate = result.dueDate
      this.applyIssuerCategory()
    },
    async startAiAnalysis() {
      if (!this.localDocument._id || this.analyzing) return

      const documentId = this.localDocument._id
      const runId = ++this.analysisRunId
      this.analysisState = "loading"
      this.analysisError = ""

      try {
        const sourceBlob = await api.documents.fetchDocumentSource(documentId)
        const fileName = this.localDocument.originalFileName || this.localDocument.title || "Dokument"
        const sourceFile = new File([sourceBlob], fileName, { type: sourceBlob.type || "application/octet-stream" })
        const formData = await documentAiService.buildAnalysisFormData(sourceFile)
        const result = await api.documents.analyzeDocument(formData)

        if (runId !== this.analysisRunId || documentId !== this.localDocument._id) return
        this.applyAnalysisResult(result)
        this.analysisState = "done"
      } catch (error) {
        if (runId !== this.analysisRunId || documentId !== this.localDocument._id) return
        this.analysisError = documentAiService.getErrorMessage(error)
        this.analysisState = "error"
        console.error("Document AI import error:", error)
      }
    },
    onSave() {
      if (this.$refs.form.validate()) {
        this.localDocument.invoiceIssuer = this.localSelectedInvoiceIssuer ? this.localSelectedInvoiceIssuer._id : null
        this.$emit("save-document", this.localDocument, this.localSelectedInvoiceIssuer)
      }
    },
    onCreateInvoiceIssuer(title) {
      this.$emit("create-new-invoice-issuer", title)
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    analyzing() {
      return this.analysisState === "loading"
    },
    analysisTitle() {
      if (this.analysisState === "loading") return "AI analysiert das Originaldokument"
      if (this.analysisState === "done") return "Erkannte Angaben wurden übernommen"
      if (this.analysisState === "error") return "AI-Import nicht möglich"
      return "Dokumentdaten automatisch importieren"
    },
    analysisCopy() {
      if (this.analysisState === "loading") return "Text und Rechnungsdaten werden erkannt. Das kann einen Moment dauern."
      if (this.analysisState === "done")
        return "Bitte kontrolliere die erkannten Angaben. Titel, Rechnungssteller und Studio bleiben unverändert."
      if (this.analysisState === "error") return this.analysisError
      return "AI ergänzt Beschreibung, Betrag, Rechnungsnummer und Daten. Titel und Rechnungssteller werden nicht verändert."
    },
    mappedStudios() {
      return this.storeStudios
        .filter((studio) => studio.code && studio.code !== "ot" && studio.code !== "alibey")
        .map((studio) => ({ _id: studio._id, name: studio.name }))
    },
  },
}
</script>

<style scoped>
.edit-dialog-card {
  --dialog-bg: rgba(28, 28, 29, 0.97);
  --dialog-surface: rgba(255, 255, 255, 0.045);
  --dialog-border: rgba(255, 255, 255, 0.09);
  --dialog-border-strong: rgba(255, 255, 255, 0.15);
  --dialog-text: #f5f5f5;
  --dialog-muted: #a3a3a3;
  display: flex;
  font-size: 1rem;
  max-height: min(90vh, 900px);
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--dialog-border-strong);
  border-radius: 22px !important;
  background: var(--dialog-bg) !important;
  backdrop-filter: blur(22px);
  box-shadow: 0 30px 90px rgba(0, 0, 0, 0.46);
}

.dialog-header {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  gap: 14px;
  padding: 24px 24px 20px;
  border-bottom: 1px solid var(--dialog-border);
}

.dialog-icon,
.section-icon {
  display: grid;
  place-items: center;
  border: 1px solid rgba(0, 150, 64, 0.24);
  background: rgba(0, 150, 64, 0.1);
  color: #65c98f;
}

.dialog-icon {
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 16px;
}

.dialog-heading {
  min-width: 0;
  flex: 1;
}

.dialog-eyebrow {
  margin-bottom: 3px;
  color: #65c98f;
  font-size: 0.75rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.dialog-heading h2 {
  margin: 0;
  color: var(--dialog-text);
  font-size: 1.25rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.dialog-heading p {
  margin: 6px 0 0;
  color: var(--dialog-muted);
  font-size: 0.875rem;
  line-height: 1.5;
}

.dialog-close {
  min-width: 44px !important;
  min-height: 44px !important;
  margin-top: -4px;
  margin-right: -8px;
}

.dialog-scroll {
  min-height: 0;
  padding: 20px 24px 26px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.ai-import-panel {
  display: flex;
  align-items: center;
  gap: 13px;
  margin-bottom: 16px;
  padding: 14px 15px;
  border: 1px solid rgba(101, 201, 143, 0.25);
  border-radius: 17px;
  background: linear-gradient(135deg, rgba(0, 150, 64, 0.12), rgba(255, 255, 255, 0.035));
}

.ai-import-panel.is-error {
  border-color: rgba(255, 152, 0, 0.36);
  background: rgba(255, 152, 0, 0.08);
}

.ai-import-icon {
  display: grid;
  flex: 0 0 44px;
  width: 44px;
  height: 44px;
  place-items: center;
  border-radius: 14px;
  background: rgba(0, 150, 64, 0.14);
}

.ai-import-copy {
  min-width: 0;
  flex: 1;
}

.ai-import-copy strong,
.ai-import-copy span {
  display: block;
}

.ai-import-copy strong {
  color: var(--dialog-text);
  font-size: 1rem;
}

.ai-import-copy span {
  margin-top: 3px;
  color: var(--dialog-muted);
  font-size: 0.875rem;
  line-height: 1.45;
}

.ai-import-button {
  flex: 0 0 auto;
  min-height: 44px !important;
  border-radius: 12px !important;
  font-size: 0.875rem !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.form-section {
  margin-bottom: 16px;
  padding: 18px;
  border: 1px solid var(--dialog-border);
  border-radius: 19px;
  background: var(--dialog-surface);
}

.form-section:last-child {
  margin-bottom: 0;
}

.section-heading {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 14px;
}

.section-icon {
  flex: 0 0 34px;
  width: 34px;
  height: 34px;
  border-radius: 11px;
}

.section-heading h3 {
  margin: 0;
  color: var(--dialog-text);
  font-size: 1rem;
  font-weight: 750;
}

.section-heading p {
  margin: 2px 0 0;
  color: var(--dialog-muted);
  font-size: 0.875rem;
}

.form-section ::v-deep .row {
  margin: -6px;
}

.form-section ::v-deep .col,
.form-section ::v-deep [class*="col-"] {
  padding: 6px;
}

.app-field ::v-deep .v-input__slot {
  min-height: 46px !important;
  border-radius: 13px !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.app-field ::v-deep fieldset {
  border-color: var(--dialog-border-strong) !important;
}

.app-field.v-input--is-focused ::v-deep fieldset {
  border-color: #009640 !important;
}

.app-field.v-input--is-focused ::v-deep .v-label,
.app-field.v-input--is-focused ::v-deep .v-icon {
  color: #65c98f !important;
}

.edit-dialog-card ::v-deep .primary--text {
  color: #009640 !important;
  caret-color: #009640 !important;
}

.dialog-footer {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  min-height: 76px;
  padding: 14px 24px calc(14px + env(safe-area-inset-bottom));
  border-top: 1px solid var(--dialog-border);
  background: rgba(22, 22, 23, 0.96);
  box-shadow: 0 -16px 34px rgba(0, 0, 0, 0.18);
}

.primary-button,
.secondary-button {
  min-height: 44px !important;
  border-radius: 13px !important;
  font-weight: 750;
  letter-spacing: 0;
  text-transform: none;
}

.primary-button:not(.v-btn--disabled) {
  background-color: #009640 !important;
  color: #fff !important;
}

@media (max-width: 599px) {
  .edit-dialog-card {
    max-height: 100%;
    border: 0;
    border-radius: 0 !important;
  }

  .dialog-header {
    padding: 18px 16px 16px;
  }

  .dialog-icon {
    flex-basis: 44px;
    width: 44px;
    height: 44px;
    border-radius: 14px;
  }

  .dialog-heading h2 {
    font-size: 1.05rem;
  }

  .dialog-heading p {
    display: none;
  }

  .dialog-scroll {
    padding: 14px 14px 20px;
  }

  .ai-import-panel {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .ai-import-button {
    width: 100%;
    flex-basis: 100%;
  }

  .form-section {
    padding: 15px;
    border-radius: 17px;
  }

  .dialog-footer {
    min-height: 72px;
    padding: 12px 14px calc(12px + env(safe-area-inset-bottom));
  }

  .dialog-footer .v-btn {
    min-width: 0 !important;
  }
}
</style>
